import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import styles from './CrawlHealthView.module.css';
import { useCrawlHealthReport } from '../../hooks/useCrawlHealthReport';
import { getErrorMessage } from '../../services/rpc';
import { useSeedSourcePrograms } from '../../hooks/useSeedSourcePrograms';
import type { CrawlHealthRow } from '../../services/rpc/crawl';

const STALE_THRESHOLD_DAYS = 14;

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const numberFormatter = new Intl.NumberFormat();

const computeDayDiff = (timestamp: string | null): number | null => {
  if (!timestamp) return null;
  const last = new Date(timestamp);
  if (Number.isNaN(last.getTime())) return null;
  const diffMs = Date.now() - last.getTime();
  return diffMs / (1000 * 60 * 60 * 24);
};

const formatDateTime = (timestamp: string | null): string => {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '—';
  return dateTimeFormatter.format(date);
};

const formatNumber = (value: number | null): string => {
  if (value == null) return '—';
  return numberFormatter.format(value);
};

const relativeText = (daysSince: number | null, noneLabel = 'No snapshots yet'): string => {
  if (daysSince == null) return noneLabel;
  if (daysSince < 1) return '< 1 day ago';
  return `${Math.floor(daysSince)} day${Math.floor(daysSince) === 1 ? '' : 's'} ago`;
};

const DEFAULT_DAYS = 7;
const DEFAULT_LIMIT = 1000; // Fetch all programs

const pipelineHelp = `Map → Review → Crawl → Health
1. Map all programs — npx ts-node scripts/run-cohort-map.ts --limit 60
2. Review tiers & overrides
3. Run critical crawls — npx ts-node scripts/run-cohort-crawl.ts --profile critical --max-depth 2
4. Check freshness — npx ts-node scripts/crawl-health-report.ts --days 7 --limit 50`;

type StepState = 'pending' | 'done' | 'active' | 'issue';

const getWorkflowStates = (row: CrawlHealthRow, daysSince: number | null): Record<'map' | 'review' | 'crawl' | 'health', StepState> => {
  const hasMapArtifacts = (row.mapLinkCount ?? 0) > 0;
  const hasMapRun = Boolean(row.lastMapRunAt);
  const hasSnapshots = row.snapshotCount > 0;
  const jobStatus = (row.lastCrawlStatus ?? '').toLowerCase();
  const crawlState: StepState = jobStatus === 'failed'
    ? 'issue'
    : jobStatus === 'completed'
      ? 'done'
      : jobStatus === 'running' || jobStatus === 'queued'
        ? 'active'
        : 'pending';
  const healthState: StepState = (() => {
    if (daysSince == null) return 'issue';
    if (daysSince <= STALE_THRESHOLD_DAYS) return 'done';
    if (daysSince <= STALE_THRESHOLD_DAYS * 2) return 'active';
    return 'issue';
  })();
  const reviewState: StepState = hasMapArtifacts && hasSnapshots ? 'done' : hasMapArtifacts ? 'active' : 'pending';
  const mapState: StepState = hasMapArtifacts ? 'done' : hasMapRun ? 'issue' : 'pending';
  if (!hasMapArtifacts && !hasSnapshots && !row.lastCrawlStatus) {
    return {
      map: 'pending',
      review: 'pending',
      crawl: 'pending',
      health: 'pending',
    };
  }

  return {
    map: mapState,
    review: reviewState,
    crawl: crawlState,
    health: healthState,
  };
};

export const CrawlHealthView: React.FC = () => {
  const [days, setDays] = useState<number>(DEFAULT_DAYS);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const [programId, setProgramId] = useState<string>('');
  const [showSeedSourceOnly, setShowSeedSourceOnly] = useState<boolean>(false);

  const queryParams = useMemo(() => ({
    days,
    limit,
    programId: programId.trim() ? programId.trim() : undefined,
  }), [days, limit, programId]);

  const query = useCrawlHealthReport(queryParams);
  const rows = useMemo(() => query.data ?? [], [query.data]);

  const seedSourceQuery = useSeedSourcePrograms();
  const seedSourcePrograms = useMemo(() => seedSourceQuery.data ?? [], [seedSourceQuery.data]);

  const seedSourceProgramIds = useMemo(() => new Set(seedSourcePrograms.map((program) => program.programId)), [seedSourcePrograms]);

  const shouldFilter = showSeedSourceOnly && seedSourceQuery.isSuccess;

  const displayedRows = useMemo(() => {
    if (!shouldFilter) return rows;
    return rows.filter((row) => seedSourceProgramIds.has(row.programId));
  }, [rows, seedSourceProgramIds, shouldFilter]);

  const emptyStateMessage = shouldFilter
    ? 'No programs found matching the seed source filter.'
    : 'No programs found within the selected window.';

  const toggleLabel = showSeedSourceOnly ? 'Seed Source Filter: On' : 'Seed Source Filter: Off';

  const isToggleDisabled = seedSourceQuery.isLoading || seedSourceQuery.isError;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>Crawl Health</h2>
          <p className={styles.subtitle}>
            Snapshot freshness, Firecrawl metrics, and job status for every program. Use this dashboard to spot stale coverage, failed jobs, or excessive credit usage before running new crawls.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
          <div className={styles.filterToggleGroup}>
            <button
              type="button"
              className={clsx(styles.toggleButton, showSeedSourceOnly && styles.toggleButtonActive)}
              onClick={() => setShowSeedSourceOnly((prev) => !prev)}
              disabled={isToggleDisabled}
              aria-pressed={showSeedSourceOnly}
            >
              {toggleLabel}
            </button>
            {seedSourceQuery.isLoading ? (
              <span className={styles.filterMeta}>Loading seed_sources…</span>
            ) : null}
            {seedSourceQuery.isError ? (
              <span className={styles.filterMetaError}>Filter unavailable. Refresh to retry.</span>
            ) : null}
            {seedSourceQuery.isSuccess ? (
              <span className={styles.filterMeta}>
                {`${seedSourcePrograms.length} active seed sources`}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className={styles.controls}>
        <div className={styles.controlGroup}>
          <label htmlFor="crawl-health-days">Window (days)</label>
          <select
            id="crawl-health-days"
            value={days}
            onChange={(event) => setDays(Number.parseInt(event.target.value, 10))}
          >
            {[7, 14, 30].map((option) => (
              <option key={option} value={option}>{option} days</option>
            ))}
          </select>
        </div>
        <div className={styles.controlGroup}>
          <label htmlFor="crawl-health-limit">Program limit</label>
          <input
            id="crawl-health-limit"
            type="number"
            min={1}
            value={limit}
            onChange={(event) => setLimit(Math.max(1, Number.parseInt(event.target.value || '1', 10)))}
          />
        </div>
        <div className={styles.controlGroup}>
          <label htmlFor="crawl-health-program">Program ID (optional)</label>
          <input
            id="crawl-health-program"
            type="text"
            placeholder="uuid…"
            value={programId}
            onChange={(event) => setProgramId(event.target.value)}
          />
        </div>
        <div className={clsx(styles.controlGroup, styles.controlGroupTextArea)}>
          <label htmlFor="crawl-health-pipeline">Pipeline quick-start</label>
          <textarea
            id="crawl-health-pipeline"
            readOnly
            value={pipelineHelp}
            rows={5}
            style={{
              width: '100%',
              padding: '0.6rem 0.7rem',
              borderRadius: '12px',
              border: '1px dashed #d1d5db',
              fontSize: '0.8rem',
              backgroundColor: '#f9fafb',
              color: '#374151',
              whiteSpace: 'pre-wrap',
              fontFamily: 'ui-monospace, SFMono-Regular, SFMono, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            }}
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
      </div>

      {query.isLoading ? (
        <div className={styles.emptyState}>Loading crawl health…</div>
      ) : query.error ? (
        <div className={styles.errorState}>{getErrorMessage(query.error)}</div>
      ) : displayedRows.length === 0 ? (
        <div className={styles.emptyState}>{emptyStateMessage}</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Program</th>
                <th style={{ minWidth: '260px' }}>Workflow</th>
                <th>Map</th>
                <th>Snapshot Summary</th>
                <th>Snapshots (last {days}d)</th>
                <th>Crawl Status</th>
                <th>Firecrawl Job ID</th>
              </tr>
            </thead>
            <tbody>
              {displayedRows.map((row) => {
                const daysSince = computeDayDiff(row.lastSnapshotAt);
                const mapDaysSince = computeDayDiff(row.lastMapRunAt);
                const workflow = getWorkflowStates(row, daysSince);
                const symbol = (state: StepState): string => {
                  if (state === 'done') return '✓';
                  if (state === 'issue') return '!';
                  if (state === 'active') return '•';
                  return '•';
                };
                const formatStep = (label: string, state: StepState): string => `${label.padEnd(7)} ${symbol(state)}`;
                const workflowSummary = [
                  formatStep('Map', workflow.map),
                  formatStep('Review', workflow.review),
                  formatStep('Crawl', workflow.crawl),
                  formatStep('Health', workflow.health)
                ].join('   ');
                return (
                  <tr key={row.programId}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#1f2937' }}>{row.programName ?? 'Unnamed program'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{row.programId}</div>
                    </td>
                    <td className={styles.workflowColumn}>
                      <div className={styles.workflowSummary}>{workflowSummary}</div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{formatNumber(row.mapLinkCount)}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        {row.lastMapRunAt ? formatDateTime(row.lastMapRunAt) : '—'}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {relativeText(mapDaysSince, 'No map run yet')}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{formatDateTime(row.lastSnapshotAt)}</div>
                      <div style={{ fontSize: '0.8rem', color: '#4b5563', marginTop: '0.2rem' }}>{relativeText(daysSince)}</div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{formatNumber(row.snapshotCountWindow)}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>All time: {formatNumber(row.snapshotCount)}</div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{row.lastCrawlStatus ?? 'Unknown'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>{row.lastCrawlJobId ?? '—'}</div>
                    </td>
                    <td>{row.firecrawlJobId ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
